import { useState, useEffect, useRef } from 'react';

/**
 * Persisted filter state keyed by (page, clientId). Survives tab navigation
 * and full page reloads via localStorage.
 *
 * Usage:
 *   const [filters, setFilters] = useFilterState('expenses', clientId, {
 *     preset: 'this-month', start: '', end: '', category: ''
 *   });
 */
export function useFilterState(pageKey, clientId, defaults) {
  const storageKey = `epicurea:filters:${pageKey}:${clientId || 'none'}`;
  const isFirstLoad = useRef(true);

  const [state, setState] = useState(() => {
    if (typeof window === 'undefined') return defaults;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch (e) { /* ignore */ }
    return defaults;
  });

  // Reset state when clientId changes (load saved state for new client)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      setState(raw ? { ...defaults, ...JSON.parse(raw) } : defaults);
    } catch (e) {
      setState(defaults);
    }
    isFirstLoad.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, pageKey]);

  // Persist on every change (skip the first hydration write)
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) { /* ignore quota errors */ }
  }, [state, storageKey]);

  return [state, setState];
}

/**
 * Returns ISO start/end dates for a preset key.
 */
export function dateRangeFromPreset(preset, customStart, customEnd) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const startOfMonth = (y, m) => fmt(new Date(y, m, 1));
  const endOfMonth = (y, m) => fmt(new Date(y, m + 1, 0));

  switch (preset) {
    case 'this-month': {
      return { start: startOfMonth(today.getFullYear(), today.getMonth()),
               end: endOfMonth(today.getFullYear(), today.getMonth()) };
    }
    case 'last-month': {
      const m = today.getMonth() - 1;
      const y = m < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const adjM = (m + 12) % 12;
      return { start: startOfMonth(y, adjM), end: endOfMonth(y, adjM) };
    }
    case 'last-30':
      return { start: fmt(new Date(Date.now() - 30 * 86400000)), end: fmt(today) };
    case 'last-90':
      return { start: fmt(new Date(Date.now() - 90 * 86400000)), end: fmt(today) };
    case 'this-year':
      return { start: fmt(new Date(today.getFullYear(), 0, 1)), end: fmt(today) };
    case 'last-year': {
      const y = today.getFullYear() - 1;
      return { start: fmt(new Date(y, 0, 1)), end: fmt(new Date(y, 11, 31)) };
    }
    case 'custom':
      return { start: customStart || '', end: customEnd || '' };
    case 'all':
    default:
      return { start: '', end: '' };
  }
}

export const PRESETS = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'last-30', label: 'Last 30 Days' },
  { value: 'last-90', label: 'Last 90 Days' },
  { value: 'this-year', label: 'This Year' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' },
];
